import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  getModuleLabel, getOperationEffect, getOperationKey, groupByModuleLabel, OperationEffect
} from '../roles.functions';

/** Composite operation item used by the picker — covers host's own ops (referenceName=null)
 *  and ref'd module ops (referenceName=set). Shape mirrors what the role-edit consumer
 *  needs back when pushing to AuthorizationRole.assignedOperations[]. */
export interface IPickerOperation {
  name: string;
  applicationReferenceName: string | null;
  description: string;
  isAuthorizable: boolean;
}

export interface IOperationSelectFormParams {
  /** Available pool the user picks from. Caller pre-filters out already-assigned ops. */
  available: IPickerOperation[];
  /** Optional context: how many roles already carry each operation, keyed by `${refName ?? ''}:${name}`. */
  assignedRoleCounts?: { [key: string]: number };
}

type EffectFilter = 'all' | 'enables' | 'restricts' | 'other';

interface PickerRow {
  key: string;
  operation: IPickerOperation;
  effect: OperationEffect;
  moduleLabel: string;
  roleCount: number | null;
}

interface PickerGroup {
  moduleLabel: string;
  rows: PickerRow[];
}

@Component({
  selector: 'app-operation-select-form',
  templateUrl: './operation-select-form.component.html',
  standalone: false
})
export class OperationSelectFormComponent implements OnInit {
  groups: PickerGroup[] = [];
  search = '';
  effectFilter: EffectFilter = 'all';
  selectedKeys = new Set<string>();

  private rows: PickerRow[] = [];

  constructor(
    private dialogRef: MatDialogRef<OperationSelectFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IOperationSelectFormParams,
  ) { }

  ngOnInit(): void {
    this.rows = (this.data.available ?? []).map(operation => {
      const key = getOperationKey(operation.applicationReferenceName, operation.name);
      return {
        key,
        operation,
        effect: getOperationEffect(operation.name),
        moduleLabel: getModuleLabel(operation.applicationReferenceName),
        roleCount: this.data.assignedRoleCounts ? (this.data.assignedRoleCounts[key] ?? 0) : null
      };
    });
    this.rebuild();
  }

  get selectedCount(): number {
    return this.selectedKeys.size;
  }

  get hasUnclassified(): boolean {
    return this.rows.some(row => row.effect === 'unknown');
  }

  onEffectFilterClicked(filter: EffectFilter) {
    this.effectFilter = filter;
    this.rebuild();
  }

  onSearchChanged() {
    this.rebuild();
  }

  isSelected(row: PickerRow): boolean {
    return this.selectedKeys.has(row.key);
  }

  onRowToggled(row: PickerRow) {
    if (this.selectedKeys.has(row.key)) {
      this.selectedKeys.delete(row.key);
    } else {
      this.selectedKeys.add(row.key);
    }
  }

  isGroupSelected(group: PickerGroup): boolean {
    return group.rows.length > 0 && group.rows.every(row => this.selectedKeys.has(row.key));
  }

  isGroupPartiallySelected(group: PickerGroup): boolean {
    return !this.isGroupSelected(group) && group.rows.some(row => this.selectedKeys.has(row.key));
  }

  onGroupToggled(group: PickerGroup) {
    const selectAll = !this.isGroupSelected(group);
    group.rows.forEach(row => selectAll ? this.selectedKeys.add(row.key) : this.selectedKeys.delete(row.key));
  }

  onSaveClicked() {
    const picked = this.rows.filter(row => this.selectedKeys.has(row.key)).map(row => row.operation);
    this.dialogRef.close(picked);
  }

  onCancelClicked() {
    this.dialogRef.close();
  }

  private rebuild() {
    let filtered = this.rows;
    if (this.effectFilter === 'other') {
      filtered = filtered.filter(row => row.effect === 'unknown');
    } else if (this.effectFilter !== 'all') {
      filtered = filtered.filter(row => row.effect === this.effectFilter);
    }
    const term = this.search?.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(row =>
        row.operation.name.toLowerCase().includes(term) ||
        row.operation.description?.toLowerCase().includes(term) ||
        row.moduleLabel.toLowerCase().includes(term));
    }

    this.groups = groupByModuleLabel(filtered, row => row.moduleLabel, row => row.operation.name);
  }
}
